import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { talkAPI } from '../services/api';
import { MessageCircle, Users, Info, Send, X, RefreshCw, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';

export const JustTalk = () => {
  const { user } = useAuth();
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [pendingMatches, setPendingMatches] = useState([]);
  const [waitingMatches, setWaitingMatches] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const endedThreadsRef = useRef(new Set()); // Track threads user has ended to prevent re-opening
  const notifiedThreadsRef = useRef(new Set()); // Track threads we've already notified about to prevent repeated toasts

  // Fetch initial availability and pending matches
  useEffect(() => {
    fetchAvailability();
    fetchPendingMatches();
    
    // Poll for new matches and messages every 5 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchPendingMatches();
      if (activeChat) {
        fetchMessages(activeChat.thread_id);
      }
    }, 5000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchAvailability = async () => {
    try {
      const response = await talkAPI.getAvailability();
      setAvailable(response.data.available);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingMatches = async () => {
    try {
      const response = await talkAPI.getPendingMatches();
      setPendingMatches(response.data.pending || []);
      setWaitingMatches(response.data.waiting || []);
      
      // Check if any active match has a thread_id (chat is ready)
      const activeMatches = response.data.active || [];
      if (activeMatches.length > 0 && activeMatches[0].thread_id && !activeChat) {
        const threadId = activeMatches[0].thread_id;
        // Don't re-open threads that user has ended in this session
        if (!endedThreadsRef.current.has(threadId)) {
          setActiveChat(activeMatches[0]);
          fetchMessages(threadId);
          
          // Only show toast if we haven't notified about this thread yet
          if (!notifiedThreadsRef.current.has(threadId)) {
            notifiedThreadsRef.current.add(threadId);
            toast.success('Chat is ready! Start talking.');
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    }
  };

  const fetchMessages = async (threadId) => {
    try {
      const response = await talkAPI.getMessages(threadId);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const toggleAvailability = async () => {
    const newAvailability = !available;
    try {
      await talkAPI.setAvailability(newAvailability, 'en');
      setAvailable(newAvailability);
      toast.success(newAvailability ? '✓ You are now available to chat' : '✗ You are now unavailable');
    } catch (error) {
      console.error('Availability toggle error:', error);
      toast.error('Failed to update availability: ' + (error.response?.data?.detail || error.message));
    }
  };

  const requestTalk = async () => {
    setSearching(true);
    try {
      const response = await talkAPI.requestTalk();
      if (response.data.match_id) {
        toast.success('Match found! Waiting for the other user to accept...');
        fetchPendingMatches();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to find match');
    } finally {
      setSearching(false);
    }
  };

  const acceptMatch = async (matchId) => {
    try {
      const response = await talkAPI.acceptMatch(matchId);
      toast.success(response.data.message);
      
      if (response.data.chat_ready && response.data.thread_id) {
        // Chat is ready, start it
        setActiveChat({ thread_id: response.data.thread_id, match_id: matchId });
        fetchMessages(response.data.thread_id);
      }
      
      fetchPendingMatches();
    } catch (error) {
      toast.error('Failed to accept match');
    }
  };

  const declineMatch = async (matchId) => {
    try {
      await talkAPI.declineMatch(matchId);
      toast.info('Match declined');
      fetchPendingMatches();
    } catch (error) {
      toast.error('Failed to decline match');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeChat?.thread_id || sendingMessage) return;
    
    setSendingMessage(true);
    const messageText = newMessage.trim();
    setNewMessage('');
    
    // Optimistically add message
    const tempMessage = {
      id: `temp-${Date.now()}`,
      sender_id: user?.user_id,
      message: messageText,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);
    
    try {
      // For now, use a simple POST endpoint (we'll need to add this)
      // In a real app, this would be via WebSocket
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/talk/threads/${activeChat.thread_id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ message: messageText })
      });
      
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      // Fetch updated messages
      fetchMessages(activeChat.thread_id);
    } catch (error) {
      toast.error('Failed to send message');
      // Remove optimistic message
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
    } finally {
      setSendingMessage(false);
    }
  };

  const endChat = async () => {
    if (activeChat?.thread_id) {
      // Track this thread as ended to prevent re-opening
      endedThreadsRef.current.add(activeChat.thread_id);
      
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/talk/threads/${activeChat.thread_id}/end`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      } catch (error) {
        console.error('Failed to end chat on server:', error);
      }
    }
    setActiveChat(null);
    setMessages([]);
    toast.info('Chat ended');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Active Chat View
  if (activeChat) {
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="just-talk-chat">
        {/* Chat Header */}
        <div className="bg-card border-b border-border px-4 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-bold text-foreground">Peer Support Chat</h1>
                <p className="text-sm text-muted-foreground">Be kind and supportive</p>
              </div>
            </div>
            <button
              onClick={endChat}
              className="px-4 py-2 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20"
              data-testid="end-chat-btn"
            >
              End Chat
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-6">
          <div className="max-w-4xl mx-auto space-y-4">
            {messages.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Start the conversation! Say hello.</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                      msg.sender_id === user?.user_id
                        ? 'bg-primary text-white rounded-br-none'
                        : 'bg-card border border-border text-foreground rounded-bl-none'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{msg.message}</p>
                    <p className={`text-xs mt-1 ${msg.sender_id === user?.user_id ? 'text-white/70' : 'text-muted-foreground'}`}>
                      {new Date(msg.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Message Input */}
        <div className="bg-card border-t border-border px-4 py-4">
          <form onSubmit={sendMessage} className="max-w-4xl mx-auto flex gap-3">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 px-4 py-3 rounded-full bg-muted border border-border text-foreground focus:ring-2 focus:ring-primary"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sendingMessage}
              className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/90 disabled:opacity-50"
              data-testid="send-message-btn"
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Main Just Talk View
  return (
    <div className="min-h-screen bg-background py-8" data-testid="just-talk-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <MessageCircle className="w-16 h-16 text-primary mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>Just Talk</h1>
          <p className="text-muted-foreground">Connect with peer supporters for meaningful conversations</p>
        </div>

        {/* Pending Match Requests - Show if someone wants to talk to this user */}
        {pendingMatches.length > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6 mb-8">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <UserCheck className="w-6 h-6 text-yellow-500" />
              Someone wants to talk to you!
            </h2>
            <div className="space-y-3">
              {pendingMatches.map((match) => (
                <div key={match.id} className="bg-card rounded-xl p-4 flex items-center justify-between border border-border">
                  <div>
                    <p className="font-semibold text-foreground">Chat Request</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(match.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => acceptMatch(match.id)}
                      className="px-4 py-2 rounded-lg bg-success text-white hover:bg-success/90"
                      data-testid="accept-match-btn"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => declineMatch(match.id)}
                      className="px-4 py-2 rounded-lg bg-muted text-foreground hover:bg-muted/80"
                      data-testid="decline-match-btn"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Waiting for acceptance */}
        {waitingMatches.length > 0 && (
          <div className="bg-info/10 border border-info/30 rounded-2xl p-6 mb-8">
            <h2 className="text-lg font-bold text-foreground mb-2 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-info animate-spin" />
              Waiting for response...
            </h2>
            <p className="text-muted-foreground">
              You've requested to talk. Waiting for the other person to accept.
            </p>
          </div>
        )}

        {/* Info Banner */}
        <div className="bg-info/10 border border-info rounded-2xl p-6 mb-8 flex items-start space-x-3">
          <Info className="w-6 h-6 text-info mt-1 flex-shrink-0" />
          <div className="text-foreground">
            <p className="font-semibold mb-2">How Just Talk Works:</p>
            <ul className="text-sm space-y-1 list-disc list-inside">
              <li>Toggle your availability to let others know you're free to chat</li>
              <li>Request a conversation and we'll match you with an available peer</li>
              <li>Both parties must accept the match before chatting begins</li>
              <li>All conversations are monitored for safety</li>
              <li>This is peer support, not professional therapy</li>
            </ul>
          </div>
        </div>

        {/* Availability Toggle */}
        <div className="bg-card rounded-2xl p-8 mb-8 card-soft border border-border text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>Your Availability</h2>
          <div className="flex items-center justify-center space-x-4 mb-6">
            <span className={`text-lg font-semibold ${available ? 'text-success' : 'text-muted-foreground'}`}>
              {available ? 'Available to Chat' : 'Unavailable'}
            </span>
            <button
              onClick={toggleAvailability}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                available ? 'bg-success' : 'bg-muted'
              }`}
              data-testid="availability-toggle"
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  available ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <p className="text-sm text-muted-foreground">
            {available 
              ? 'Others can now match with you for conversations' 
              : 'Toggle on to become available for peer support'}
          </p>
        </div>

        {/* Request Talk */}
        <div className="bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-8 text-white text-center">
          <Users className="w-16 h-16 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>Start a Conversation</h2>
          <p className="mb-6">We'll match you with someone who's available to chat right now</p>
          <button
            onClick={requestTalk}
            disabled={searching || waitingMatches.length > 0}
            className="px-8 py-4 rounded-full bg-white text-primary font-bold hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100"
            data-testid="request-talk-button"
          >
            {searching ? 'Searching...' : waitingMatches.length > 0 ? 'Already Searching...' : 'Find Someone to Talk'}
          </button>
        </div>

        {/* Safety Notice */}
        <div className="mt-8 bg-card rounded-2xl p-6 card-soft border border-border">
          <h3 className="font-bold text-foreground mb-3">Safety & Guidelines</h3>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li>• Be respectful and supportive</li>
            <li>• Don't share personal information (phone numbers, addresses, etc.)</li>
            <li>• This is not professional therapy - encourage seeking professional help when needed</li>
            <li>• Report any concerning behavior immediately</li>
            <li>• If someone mentions self-harm, encourage them to use crisis resources</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
